/**
 * CARRYING A JUST-SAVED CARD VALUE ACROSS THE SUBSCRIPTION RECONCILE.
 *
 * The issues watcher REPLACES the on-screen list with the server's snapshot on
 * every read. Right after a BURST of writes — a bulk "Assign to" sets Responsible
 * on several cards at once — the batched read-back can return a SUBSET of those
 * cards as they were just BEFORE the write (read-after-write lag), so the watcher
 * paints them with the OLD value and the just-set person visibly disappears, then
 * reappears on the next read (interaction-witness, 27 Oct; ~2 of 3 bulk runs,
 * measured). The write itself succeeds — the database holds the new value the
 * whole time, every per-card PATCH returned 200 — only the screen regresses.
 *
 * The fix mirrors carryPendingAreas: the store stamps each card it writes PENDING
 * with the `updatedAt` it set. On each snapshot, a pending card whose incoming row
 * is OLDER than that stamp is a stale read-back, so the newer local copy is kept.
 * The pending mark clears the moment a read is at least as new as the write (the
 * server has caught up) or after a short safety window, so a card can never be
 * held against the server indefinitely. By id and by version: the server always
 * wins once it carries the write, and a card that is NOT pending is never carried,
 * so an edit from elsewhere still takes effect — the load-bearing property the
 * areas reconcile relies on too.
 *
 * Pure: it reads the pending stamps but mutates nothing. The caller drops the
 * `cleared` ids from its own pending map. Unit-tested in
 * scripts/test-issue-reconcile.ts.
 */

/** How long a just-written card may keep its local copy against a stale read
 *  before the server snapshot is trusted regardless. Comfortably longer than the
 *  measured read-after-write lag (a few seconds) and short enough that a genuine
 *  divergence (which should not happen — the DB holds the write) heals quickly. */
export const PENDING_WRITE_WINDOW_MS = 20_000;

export interface Versioned {
  id: string;
  /** Milliseconds; a card with no stamp is treated as the oldest possible. */
  updatedAt?: number;
}

/**
 * Merge a freshly-read issues list over what is on screen so a lagged read-back
 * cannot walk back a card THIS client just saved.
 *
 * @param server   the snapshot the watcher is about to show (full read or splice)
 * @param current  what is on screen now (carries this client's optimistic writes)
 * @param pending  id → the `updatedAt` stamp set when this client last wrote the card
 * @param now      Date.now(), passed in so the function stays pure and testable
 * @returns the list to show, and the pending ids the caller should drop (settled
 *          because the server caught up, or expired past the safety window)
 */
export function carryPendingCardWrites<T extends Versioned>(
  server: T[],
  current: readonly T[],
  pending: ReadonlyMap<string, number>,
  now: number,
): { issues: T[]; cleared: string[] } {
  // Nothing outstanding — show the server snapshot exactly, same array reference,
  // no per-row work. This is the overwhelmingly common path.
  if (pending.size === 0) return { issues: server, cleared: [] };

  const currentById = new Map(current.map((i) => [i.id, i]));
  const cleared: string[] = [];
  const issues = server.map((incoming) => {
    const stamp = pending.get(incoming.id);
    if (stamp === undefined) return incoming; // not ours / not pending — server wins
    const caughtUp = (incoming.updatedAt ?? 0) >= stamp;
    const expired = now - stamp >= PENDING_WRITE_WINDOW_MS;
    if (caughtUp || expired) {
      cleared.push(incoming.id); // settled — stop guarding this card
      return incoming;
    }
    // A stale read-back of a card we just saved: keep the newer local copy.
    return currentById.get(incoming.id) ?? incoming;
  });
  return { issues, cleared };
}
